import type { TimezoneComparison } from "@multisystemsuite/timezone-engine-shared-types";
import { getTimezoneOffsetMinutes, toUTC } from "./conversion.js";

function formatOffsetDifference(minutes: number): string {
  const abs = Math.abs(minutes);
  const hours = Math.floor(abs / 60);
  const mins = abs % 60;
  const sign = minutes >= 0 ? "+" : "-";
  return `${sign}${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
}

/** Compare two timezones */
export function compareTimezones(
  source: string,
  target: string,
  date: Date | string | number = new Date(),
): TimezoneComparison {
  const sourceOffset = getTimezoneOffsetMinutes(source, date);
  const targetOffset = getTimezoneOffsetMinutes(target, date);
  const diff = targetOffset - sourceOffset;

  return {
    source,
    target,
    offsetDifferenceMinutes: diff,
    offsetDifferenceLabel: formatOffsetDifference(diff),
    sameOffset: diff === 0,
    sourceAhead: diff < 0,
  };
}

/** Compare multiple timezones against a reference */
export function compareMultipleTimezones(
  reference: string,
  targets: string[],
  date: Date | string | number = new Date(),
): TimezoneComparison[] {
  return targets.map((target) => compareTimezones(reference, target, date));
}

/** Find best meeting time across timezones (simple overlap window) */
export function findCommonWorkingHours(
  timezones: string[],
  workStartHour = 9,
  workEndHour = 17,
  date: Date = new Date(),
): { startHourUTC: number; endHourUTC: number; overlapHours: number } {
  if (timezones.length === 0) {
    return { startHourUTC: workStartHour, endHourUTC: workEndHour, overlapHours: 0 };
  }

  const offsets = timezones.map((tz) => getTimezoneOffsetMinutes(tz, date));
  const minOffset = Math.min(...offsets);
  const maxOffset = Math.max(...offsets);

  const startUTC = workStartHour - maxOffset / 60;
  const endUTC = workEndHour - minOffset / 60;
  const overlap = Math.max(0, endUTC - startUTC);

  return {
    startHourUTC: startUTC,
    endHourUTC: endUTC,
    overlapHours: overlap,
  };
}

function toDate(input: Date | string | number): Date {
  if (input instanceof Date) return input;
  return new Date(input);
}

/** Timezone-safe instant comparison — compares UTC instants, not wall-clock strings */
export function timezoneSafeCompare(
  a: Date | string | number,
  b: Date | string | number,
  timezoneA = "UTC",
  timezoneB = "UTC",
): { equal: boolean; differenceMs: number; aUTC: Date; bUTC: Date } {
  const aUTC = toUTC(toDate(a), timezoneA);
  const bUTC = toUTC(toDate(b), timezoneB);
  const differenceMs = aUTC.getTime() - bUTC.getTime();
  return {
    equal: differenceMs === 0,
    differenceMs,
    aUTC,
    bUTC,
  };
}
